import React from "react";
import { Tooltip } from "@mui/material";
import Canvas from "../../AnimationTimeLinePanel/Canvas";
import Dragger from "../../../images/Dragger.svg";
import keyframeMarkerIcon from "../../../images/EditorAnimation/keyframeMarker.svg";
import { shouldBlockHitAreaRenameSpaceKey, HIT_AREA_TIMELINE_KEYFRAME_CLIP_PAD } from "./utils";

const BOUNDARY_WARNING_LABEL = "Too close to boundary!";
const LAYER_NAME_TOOLTIP_MIN_LENGTH = 10;

const wrapLayerNameTooltip = (layerName, nameElement) => {
  const resolvedLayerName = String(layerName || "");
  if (resolvedLayerName.length <= LAYER_NAME_TOOLTIP_MIN_LENGTH) {
    return nameElement;
  }

  return (
    <Tooltip
      title={resolvedLayerName}
      placement="top-start"
      arrow
      classes={{
        popper: "hitAreaLayerNameTooltip",
        tooltip: "clickableAreasTooltip",
      }}
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [-4, -6],
            },
          },
        ],
      }}
    >
      {nameElement}
    </Tooltip>
  );
};

export default function HitAreaTimelineSection({
  timelinePanelRef,
  timelineText,
  playIcon,
  pauseIcon,
  isTimelinePlaying,
  onToggleTimelinePlayback,
  canAddKeyframeAtPlayhead,
  showAddKeyframeButton,
  addKeyframeIcon,
  onAddKeyframe,
  timelineRows,
  editingHitAreaId,
  editingHitAreaName,
  maxHitAreaNameLength,
  onHitAreaRenameInputChange,
  finishHitAreaRename,
  beginHitAreaRename,
  handleTimelineRowClick,
  toggleHitAreaLockById,
  toggleHitAreaVisibilityById,
  dragImage,
  imageCompIcon,
  hitAreaCompIcon,
  lockIcon,
  unlockIcon,
  hideIcon,
  eyeIcon,
  timelineLeftColumnRef,
  handleTimelineLeftScroll,
  timelineRightColumnRef,
  handleTimelineRightScroll,
  timelineRightInnerRef,
  safeTimelineWidth,
  timelineLeftInset,
  timelineScale,
  draggerWidth,
  setDraggerWidth,
  isResizing,
  setIsResizing,
  modalCanvasAppData,
  timelineMarkerLeft,
  timelineMarkerRef,
  beginTimelineMarkerDrag,
  currentTimelineSecond,
  secondWidth,
  minTimelineDuration,
  beginTimelineRangeEdit,
  openKeyframePopupFromTimelineEventMarker,
  powerIcon,
  hasNearbyKeyframeSpacing,
  clamp,
  roundTimelineTime,
  beginTimelineKeyframeDrag,
  selectTimelineKeyframe,
}) {
  const [timelineCurrentTime, timelineTotalTime] = timelineText.split(" / ");

  // Timeline rendering includes controls, row metadata, ranges, and keyframe editor.
  return (
    <div
      className={`hitAreaTimelinePanel ${isTimelinePlaying ? "isTimelinePlaying" : ""}`}
      ref={timelinePanelRef}
    >
      <div className="hitAreaTimelineTitle">TIMELINE</div>
      <div className="hitAreaTimelineLayout">
        {/* Left column: time info, keyframe controls, and row-level actions. */}
        <div
          className="hitAreaTimelineLeftColumn"
          ref={timelineLeftColumnRef}
          onScroll={handleTimelineLeftScroll}
        >
          <div className="hitAreaTimelineLeftHead">
            <span className="hitAreaTimelineTime">
              <span className="hitAreaTimelineTimeCurrent">{timelineCurrentTime}</span>
              <span className="hitAreaTimelineTimeTotal">
                {timelineTotalTime ? ` / ${timelineTotalTime}` : ""}
              </span>
            </span>
            {showAddKeyframeButton && (
              <Tooltip title="Add Keyframe" placement="top" arrow>
                <span className="hitAreaTimelineAddKeyframeButtonWrap">
                  <button
                    type="button"
                    className={`hitAreaTimelineAddKeyframeButton ${canAddKeyframeAtPlayhead ? "" : "isDisabled"}`}
                    aria-label="Add Keyframe"
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!canAddKeyframeAtPlayhead) return;
                      onAddKeyframe(event.currentTarget);
                    }}
                    disabled={!canAddKeyframeAtPlayhead}
                  >
                    <img
                      src={addKeyframeIcon}
                      alt=""
                      className="hitAreaTimelineAddKeyframeIcon"
                    />
                  </button>
                </span>
              </Tooltip>
            )}
            <Tooltip
              title={isTimelinePlaying ? "Pause" : "Play"}
              placement="top"
              arrow
            >
              <button
                type="button"
                className="hitAreaTimelinePlayButton"
                aria-label={isTimelinePlaying ? "Pause" : "Play"}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleTimelinePlayback();
                }}
              >
                <img
                  src={isTimelinePlaying ? pauseIcon : playIcon}
                  alt={isTimelinePlaying ? "Pause" : "Play"}
                  className={`hitAreaTimelinePlay ${isTimelinePlaying ? "isPause" : ""}`}
                />
              </button>
            </Tooltip>
          </div>
          {/* Layer list supports selection, rename, lock, visibility, and warnings. */}
          {timelineRows.map((row) => {
            const isHitAreaRow = row.type === "hit-area";
            const isRenaming = isHitAreaRow && editingHitAreaId === row.id;
            return (
              <div
                key={`${row.id}-left`}
                className={`hitAreaTimelineLayerRow ${row.isSelected ? "isSelected" : ""} ${row.isHidden ? "isHidden" : ""} ${row.isLocked ? "isLocked" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleTimelineRowClick(row)}
                onKeyDown={(event) => {
                  if (isRenaming) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleTimelineRowClick(row);
                  }
                }}
              >
                {(isHitAreaRow || row.type === "image") && row.isSelected ? (
                  <img src={dragImage} alt="Drag handle" className="hitAreaTimelineDrag" />
                ) : (
                  <span className="hitAreaTimelineDrag isBlank" aria-hidden="true" />
                )}
                <img
                  src={isHitAreaRow ? hitAreaCompIcon : imageCompIcon}
                  alt={isHitAreaRow ? "Hit area" : "Image layer"}
                  className="hitAreaTimelineLayerIcon"
                />
                <div className="hitAreaTimelineLayerContent">
                {isRenaming ? (
                  <input
                    autoFocus
                    type="text"
                    value={editingHitAreaName}
                    maxLength={maxHitAreaNameLength}
                    className="hitAreaTimelineNameInput"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) =>
                      onHitAreaRenameInputChange(event.target.value)
                    }
                    onKeyDown={(event) => {
                      event.stopPropagation();
                      if (event.key === " ") {
                        const input = event.currentTarget;
                        if (
                          shouldBlockHitAreaRenameSpaceKey(
                            editingHitAreaName,
                            input.selectionStart,
                            input.selectionEnd
                          )
                        ) {
                          event.preventDefault();
                          return;
                        }
                      }
                      if (event.key === "Enter") {
                        event.preventDefault();
                        finishHitAreaRename(true);
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        finishHitAreaRename(false);
                      }
                    }}
                    onBlur={() => finishHitAreaRename(true)}
                  />
                ) : (
                  <div className="hitAreaTimelineNameWrap">
                    {wrapLayerNameTooltip(
                      row.name,
                      <span
                        className={`hitAreaTimelineLayerName ${isHitAreaRow ? "isEditable" : ""}`}
                        onDoubleClick={(event) => beginHitAreaRename(event, row)}
                      >
                        {row.name}
                      </span>
                    )}
                    {isHitAreaRow && row.isBoundaryWarning && (
                      row.isSelected ? (
                        <Tooltip
                          title={BOUNDARY_WARNING_LABEL}
                          placement="top"
                          arrow
                          classes={{
                            popper: "clickableAreasTooltip",
                            tooltip: "clickableAreasTooltip",
                          }}
                        >
                          <span className="hitAreaTimelineBoundaryWarning">
                            {`\u26A0\uFE0F ${BOUNDARY_WARNING_LABEL}`}
                          </span>
                        </Tooltip>
                      ) : (
                        <span className="hitAreaTimelineBoundaryWarning">
                          {`\u26A0\uFE0F ${BOUNDARY_WARNING_LABEL}`}
                        </span>
                      )
                    )}
                  </div>
                )}
                {(isHitAreaRow || row.type === "image") &&
                  row.isSelected &&
                  !isTimelinePlaying && (
                  <div className="hitAreaTimelineLayerActions">
                    <div className="hitAreaTimelineLayerControlGroup">
                      {isHitAreaRow ? (
                        <Tooltip
                          title={row.isLocked ? "UnLock Layer" : "Lock Layer "}
                          placement="top"
                        >
                          <button
                            type="button"
                            className="hitAreaTimelineIconButton hitAreaTimelineLockButton"
                            disabled={isTimelinePlaying}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              if (isTimelinePlaying) {
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                              }
                              toggleHitAreaLockById(row.id, event);
                            }}
                            aria-label={row.isLocked ? "UnLock Layer" : "Lock Layer"}
                          >
                            <img
                              src={row.isLocked ? lockIcon : unlockIcon}
                              alt={row.isLocked ? "Locked" : "Unlocked"}
                              className="hitAreaTimelineLock"
                            />
                          </button>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={row.isLocked ? "UnLock Layer" : "Lock Layer "}
                          placement="top"
                        >
                          <span
                            className="hitAreaTimelineIconButton hitAreaTimelineLockButton isReadOnly"
                            aria-label={row.isLocked ? "UnLock Layer" : "Lock Layer"}
                          >
                            <img
                              src={row.isLocked ? lockIcon : unlockIcon}
                              alt={row.isLocked ? "Locked" : "Unlocked"}
                              className="hitAreaTimelineLock"
                            />
                          </span>
                        </Tooltip>
                      )}
                      {/* {isHitAreaRow ? (
                        <Tooltip
                          title={row.isHidden ? "Show Layer" : "Hide Layer "}
                          placement="top"
                        >
                          <button
                            type="button"
                            className="hitAreaTimelineIconButton hitAreaTimelineVisibilityButton"
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onClick={(event) =>
                              toggleHitAreaVisibilityById(row.id, event)
                            }
                            aria-label={row.isHidden ? "Show Layer" : "Hide Layer"}
                          >
                            <img
                              src={row.isHidden ? hideIcon : eyeIcon}
                              alt={row.isHidden ? "Hidden" : "Visible"}
                              className="hitAreaTimelineVisibility"
                            />
                          </button>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={row.isHidden ? "Show Layer" : "Hide Layer "}
                          placement="top"
                        >
                          <span
                            className="hitAreaTimelineIconButton hitAreaTimelineVisibilityButton isReadOnly"
                            aria-label={row.isHidden ? "Show Layer" : "Hide Layer"}
                          >
                            <img
                              src={row.isHidden ? hideIcon : eyeIcon}
                              alt={row.isHidden ? "Hidden" : "Visible"}
                              className="hitAreaTimelineVisibility"
                            />
                          </span>
                        </Tooltip>
                      )} */}
                    </div>
                  </div>
                )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right column: fixed ruler canvas + vertically scrolling layer tracks. */}
        <div className="hitAreaTimelineRightColumn">
          <div
            className="hitAreaTimelineRightBody"
            ref={timelineRightInnerRef}
          >
            <div
              className="hitAreaTimelineRightInner"
              style={{ width:"900px" }}
            >
              <div
                className="hitAreaTimelineMarker"
                ref={timelineMarkerRef}
                style={{ left: `${Math.min(draggerWidth+5,timelineLeftInset + timelineMarkerLeft)}px` }}
                title={`${currentTimelineSecond.toFixed(1)}s`}
              >
                <img
                  src={keyframeMarkerIcon}
                  alt=""
                  className="hitAreaTimelineMarkerHead"
                  draggable={false}
                  onMouseDown={beginTimelineMarkerDrag}
                />
                <span className="hitAreaTimelineMarkerTail" aria-hidden="true" />
              </div>
              <div
                className="hitAreaTimelineRightRuler"
                style={{
                  marginLeft: timelineLeftInset,
                  width: safeTimelineWidth,
                }}
              >
                <Canvas
                  totalDuration={timelineScale}
                  draggerWidth={draggerWidth}
                  setDraggerWidth={setDraggerWidth}
                  isResizing={isResizing}
                  setIsResizing={setIsResizing}
                  setMousUpOnTimeline={() => {}}
                  movedOnMarker={false}
                  appData={modalCanvasAppData}
                  playPause={{ animationCompleted: true }}
                  domIdPrefix="hit-area-"
                />
              </div>
              <div
                className="hitAreaTimelineRightTracks"
                ref={timelineRightColumnRef}
                onScroll={handleTimelineRightScroll}
                style={{
                  // Extend clip region left so 0s keyframe diamonds aren't cut off,
                  // while keeping range/content aligned with the ruler inset.
                  marginLeft:
                    timelineLeftInset - HIT_AREA_TIMELINE_KEYFRAME_CLIP_PAD,
                  paddingLeft: HIT_AREA_TIMELINE_KEYFRAME_CLIP_PAD,
                  width:
                    safeTimelineWidth + HIT_AREA_TIMELINE_KEYFRAME_CLIP_PAD,
                  boxSizing: "border-box",
                }}
              >
            {/* Each row renders its active range, event markers, and keyframe diamonds. */}
            {timelineRows.map((row) => {
              const hasKeyframes =
                row.type === "hit-area" && (row.keyframes || []).length > 0;

              return (
              <div
                key={`${row.id}-right`}
                className={`hitAreaTimelineRangeTrack ${row.type === "hit-area" ? "isHitArea" : "isImage"} ${row.isSelected ? "isSelected" : ""} ${row.isHidden ? "isHidden" : ""} ${row.isLocked ? "isLocked" : ""}`}
                onMouseDown={(event) => {
                  if (event.target.closest(".hitAreaTimelineEventMarker")) return;
                  handleTimelineRowClick(row);
                }}
              >
                <div
                  className={`hitAreaTimelineRangeBar ${row.type === "hit-area" ? "isHitArea" : "isImage"} ${row.isSelected ? "isSelected" : ""} ${row.isHidden ? "isHidden" : ""} ${row.isLocked ? "isLocked" : ""}`}
                  style={{
                    left: `${row.timelineStart * secondWidth}px`,
                    width: `${Math.max(
                      minTimelineDuration * secondWidth,
                      (row.timelineEnd - row.timelineStart) * secondWidth
                    )}px`,
                  }}
                  onMouseDown={(event) => {
                    if (event.target.closest(".hitAreaTimelineEventMarker")) return;
                    if (row.type !== "hit-area") return;
                    // Range bar is selectable only — horizontal drag/move is disabled.
                    event.stopPropagation();
                    handleTimelineRowClick(row);
                  }}
                >
                  {row.type === "hit-area" && (
                    <>
                      {(row.isSelected || hasKeyframes) && (
                        <>
                      <div
                        className={`hitAreaTimelineEventMarker start ${hasKeyframes ? "hasKeyframes" : ""}`}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (isTimelinePlaying) return;
                          openKeyframePopupFromTimelineEventMarker(event, row, "start");
                        }}
                      >
                        {!hasKeyframes && row.isSelected && (
                          <img
                            src={powerIcon}
                            alt="On Click start"
                            className="hitAreaTimelineEventIcon"
                          />
                        )}
                      </div>
                      <div
                        className={`hitAreaTimelineEventMarker end ${hasKeyframes ? "hasKeyframes" : ""}`}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (isTimelinePlaying) return;
                          openKeyframePopupFromTimelineEventMarker(event, row, "end");
                        }}
                      >
                        {!hasKeyframes && row.isSelected && (
                          <img
                            src={powerIcon}
                            alt="On Click end"
                            className="hitAreaTimelineEventIcon"
                          />
                        )}
                        {/* <span className="hitAreaTimelineEventPause" aria-hidden="true">
                          {"\u2225"}
                        </span> */}
                      </div>
                        </>
                      )}
                      {(() => {
                        return (row.keyframes || []).map((keyframe) => {
                          const duration = Math.max(
                            minTimelineDuration,
                            row.timelineEnd - row.timelineStart
                          );
                          const markerPercent = clamp(
                            ((keyframe.time - row.timelineStart) / duration) * 100,
                            0,
                            100
                          );
                          const isSelectedKeyframe =
                            String(row.selectedKeyframeId) === String(keyframe.id);
                          const hasVeryCloseSibling = hasNearbyKeyframeSpacing(
                            row.keyframes || [],
                            keyframe.time,
                            keyframe.id
                          );
                          return (
                            <button
                              key={keyframe.id}
                              type="button"
                              className={`hitAreaTimelineKeyframeMarker ${isSelectedKeyframe ? "isSelected" : ""}`}
                              style={{
                                left: `${markerPercent}%`,
                              }}
                              title={
                                hasVeryCloseSibling
                                  ? `${row.name} keyframe @ ${roundTimelineTime(
                                      keyframe.time
                                    ).toFixed(
                                      1
                                    )}s (very close to another keyframe; zoom timeline to disambiguate)`
                                  : `${row.name} keyframe @ ${roundTimelineTime(
                                      keyframe.time
                                    ).toFixed(1)}s`
                              }
                              onMouseDown={(event) => {
                                if (isTimelinePlaying) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  return;
                                }
                                beginTimelineKeyframeDrag(event, row, keyframe);
                              }}
                              onClick={(event) => {
                                if (isTimelinePlaying) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  return;
                                }
                                selectTimelineKeyframe(event, row, keyframe);
                              }}
                            >
                              <span
                                className="hitAreaTimelineKeyframeDiamond"
                                aria-hidden="true"
                              />
                            </button>
                          );
                        });
                      })()}
                      <img
                        src={Dragger}
                        alt=""
                        className="img-fluid hitAreaTimelineHandle draggerIcon2 right"
                        onMouseDown={(event) => {
                          event.stopPropagation();
                          beginTimelineRangeEdit(event, row, "resize-end");
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
              );
            })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
